import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';
import ScrollToTop from './components/common/ScrollToTop';
import RouteAnalytics from './components/common/RouteAnalytics';
import ErrorBoundary from './components/common/ErrorBoundary';
import { WhatsAppFloatingButton } from './components/common/WhatsAppFloatingButton';
import { ConsultaModal } from './components/common/ConsultaModal';
import { ConsultaModalProvider } from './context/ConsultaModalContext';
import { trackConversion } from './lib/analytics';

/**
 * O botão flutuante de WhatsApp é global, mas sai de duas rotas: em /links ele
 * fica em cima do botão principal da própria página (dois WhatsApp na mesma
 * tela), e /portal é ferramenta interna — convite para falar com o escritório
 * ali é ruído, e ainda por cima mandaria o próprio escritório para o próprio
 * WhatsApp.
 */
const BotaoWhatsAppGlobal = () => {
    const { pathname } = useLocation();
    if (pathname === '/links' || pathname === '/portal') return null;
    return (
        <WhatsAppFloatingButton
            phoneNumber="5511969241378"
            initialMessage="Olá! Gostaria de saber mais sobre os serviços da Pacces Advogados."
            tooltipText="Falar com um advogado"
            onClickCallback={() => trackConversion('sp', 'whatsapp')}
        />
    );
};


// Pages
import { LandingPage } from './pages/LandingPage';
import { ServicePage } from './pages/ServicePage';
import { PrivacyPage } from './pages/PrivacyPage';
import { AdLandingPage } from './pages/AdLandingPage';
import { ConteudoPage } from './pages/ConteudoPage';
import { ArtigoPage } from './pages/ArtigoPage';
import { LinksPage } from './pages/LinksPage';

/**
 * O portal entra por carregamento tardio DE PROPÓSITO.
 *
 * Ele arrasta o SDK do Firebase (~200 KB). O site institucional e as landing
 * pages de anúncio não usam nada disso, e a imagem do hero das LPs é o elemento
 * de LCP — somar esse peso ao bundle comum pioraria a métrica em toda página
 * para servir duas pessoas. Assim o custo fica só em /portal.
 */
const PortalPage = lazy(() => import('./pages/PortalPage'));

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/areas/:slug" element={<ServicePage />} />
        <Route path="/servicos/:slug" element={<ServicePage />} />
        <Route path="/services" element={<ServicePage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        {/* Conteúdo editorial. Substitui o antigo /blog, que era rota morta. */}
        <Route path="/conteudo" element={<ConteudoPage />} />
        <Route path="/conteudo/:slug" element={<ArtigoPage />} />
        {/* Landing pages de anúncio — fora da navegação do site e noindex. */}
        <Route path="/lp/:slug" element={<AdLandingPage />} />
        {/* Destino do link da bio. noindex, fora do sitemap. */}
        <Route path="/links" element={<LinksPage />} />
        {/* Área restrita: controle dos contratos vindos dos anúncios. */}
        <Route
          path="/portal"
          element={
            <Suspense fallback={<div className="min-h-screen bg-stone-50" />}>
              <PortalPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ConsultaModalProvider>
          <Router>
            <ScrollToTop />
            <RouteAnalytics />
            <AnimatedRoutes />
            <BotaoWhatsAppGlobal />
            <ConsultaModal />
          </Router>
        </ConsultaModalProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}


export default App;
